import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import moment, { Moment } from 'moment';
import momentTz from 'moment-timezone';
import { MuniEnvironmentParams } from '@ipreo/muni-shared';
import { sortBy } from 'lodash';
import { EnvironmentConfig, ENVIRONMENT_CONFIG } from '@ipreo/northstar';
import {
  CalendarResponse,
  Event,
  EventData,
  EventValue
} from '../models/calendar';

const ECONODAY_ATTRIBUTE_MAP: Map<number, string> = new Map<number, string>(
  new Array<[number, string]>(
    [1, 'Actual Available'],
    [2, 'Consensus Available'],
    [4, 'Market Mover'],
    [8, 'Highlight Available'],
    [16, 'Non Timed'],
    [32, 'Commentary Item'],
    [64, 'Merit extra Attention'],
    [128, 'Revised Data Available'],
    [256, 'Gov. Shutdown Affected event'],
    [512, 'Other Key Indicator']
  )
);

const ECONODAY_FREQUENCY_MAP: Map<number, string> = new Map<number, string>(
  new Array<[number, string]>(
    [0, 'None Specified'],
    [1, 'Weekly'],
    [2, 'Monthly'],
    [3, 'Quarterly'],
    [4, 'Every Other Week'],
    [5, 'About Every 6 weeks']
  )
);

export const DEFAULT_TIMEZONE = 'America/New_York';

@Injectable()
export class CalendarService {
  private muniApiHost: string;

  constructor(
    private readonly http: HttpClient,
    @Inject(ENVIRONMENT_CONFIG) config: EnvironmentConfig
  ) {
    this.muniApiHost = (config.params as MuniEnvironmentParams).apiHost;
  }

  public fetchData(date: Date): Observable<Event[]> {
    const requestedDate: string = moment(date).format('MM-DD-YYYY');

    return this.http
      .get(`${this.muniApiHost}/api/v1/economic-events/${requestedDate}`)
      .pipe(map((res: CalendarResponse) => this.eventsToViewModel(res)));
  }

  public eventsToViewModel(response: CalendarResponse): Event[] {
    return sortBy<Event>(
      response.events.map(this.eventToViewModel.bind(this)),
      'releasedAt'
    );
  }

  public calculateDates(date: Moment): Moment[] {
    const previousWeekday = this.previousWeekday(date.clone());
    const previousWeekday2 = this.previousWeekday(previousWeekday.clone());
    const previousWeekday3 = this.previousWeekday(previousWeekday2.clone());

    const nextWeekday = this.nextWeekday(date.clone());
    const nextWeekday2 = this.nextWeekday(nextWeekday.clone());
    const nextWeekday3 = this.nextWeekday(nextWeekday2.clone());

    return [
      previousWeekday3,
      previousWeekday2,
      previousWeekday,
      date.clone(),
      nextWeekday,
      nextWeekday2,
      nextWeekday3
    ];
  }

  public parseEventDate(dateString: string): Date {
    return momentTz.tz(dateString, DEFAULT_TIMEZONE).toDate();
  }

  private eventToViewModel(event: EventData): Event {
    const attributes = this.reduceHashValue(
      parseInt(event.attribute, 10),
      ECONODAY_ATTRIBUTE_MAP,
      512
    );

    return {
      id: event.id,
      name: event.name,
      description: event.description,
      definition: event.definition,
      highlights: event.highlights,
      createdAt: this.parseEventDate(event.createdAt),
      updatedAt: this.parseEventDate(event.updatedAt),
      releasedAt: this.parseEventDate(event.releasedAt),
      releaseFor: event.releaseFor,
      values: this.valuesToViewModel(event.values),
      attributes,
      frequency: ECONODAY_FREQUENCY_MAP.get(+event.frequency),
      nonTimed: moment(event.releasedAt).hour() === 0
    };
  }

  private reduceHashValue(
    value: number,
    mapVal: Map<number, string>,
    currentKey: number
  ): any[] {
    if (value === 0) {
      return [];
    }

    const result: any[] = this.reduceHashValue(
      value % currentKey,
      mapVal,
      currentKey / 2
    );

    if (value / currentKey >= 1) {
      result.push(mapVal.get(currentKey));
    }

    return result;
  }

  private valuesToViewModel(values: string): EventValue[] {
    if (values) {
      const valueData = JSON.parse(values)['VALUES']['VALUE']; // tslint:disable-line:no-string-literal

      return [].concat(valueData).map(this.valueToViewModel);
    }

    return [];
  }

  private valueToViewModel(value: any): EventValue {
    // tslint:disable:no-string-literal
    return {
      name: value['VALUE_NAME'],
      previous: value['PREVIOUS'],
      consensus: value['CONSENSUS'],
      actual: value['ACTUAL'],
      revised: value['REVISED'],
      revised_hist: value['REVISEDHIST'],
      prefix: value['PREFIX'],
      suffix: value['SUFFIX'],
      uid: value['VALUE_UID']
    };
  }

  private previousWeekday(date: Moment): Moment {
    switch (date.isoWeekday()) {
      // If it is Monday (1) or Sunday (7), Get the previous Friday (5)
      // and ensure we are on the previous week
      case 1:
      case 7:
        return date.subtract(6, 'days').isoWeekday(5);
      // If it any other weekend, just return the previous day
      default:
        return date.subtract(1, 'day');
    }
  }

  private nextWeekday(date: Moment): Moment {
    switch (date.isoWeekday()) {
      // If it is Saturday (6) or Friday (5), Get the next Monday (1)
      // and ensure we are on the next week
      case 5:
      case 6:
        return date.add(6, 'days').isoWeekday(1);
      // If it any other day, just return the next day
      default:
        return date.add(1, 'day');
    }
  }
}
